import { Box, Stack, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC, ReactNode, useCallback } from 'react';

import { DateTime } from '../../utility/luxon';
import Icon24 from '../shared/icon-24';
import MuviTabs from '../shared/muvi-tabs';
import { Session } from '@/logic/services/my-booking';
import isNumber from 'lodash/isNumber';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';
import { useSnack } from '@/logic/state';

interface TimesFilterProps {
  sessions: Session[];
  activeTab: number | boolean;
  handleClick: (sessionId: number, isAfterMidnight: boolean, isThreeD: boolean) => void;
  experienceName?: string;
}

export const TimesFilter: FC<TimesFilterProps> = ({
  sessions,
  activeTab,
  handleClick,
  experienceName,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();
  const setSnack = useSnack(s => s.setSnack, shallow);

  const checkIfAfterMidNight = useCallback(
    (_session: Session) => {
      const time24 = Number(DateTime.fromISO(_session.showtime).toFormat('HH'));
      if (time24 >= 0 && time24 < 6) {
        return intl.formatMessage({ id: 'experience.after.midnight' });
      } else return '';
    },
    [intl]
  );

  const handleSelectTime = (session: Session, state: boolean): void => {
    if (session.isDisabled) {
      showDisabledShowTimeMessage();
    } else {
      handleClick(session.id, state, session.isThreeD);
    }
  };

  const showDisabledShowTimeMessage = () => {
    setSnack({
      isOpen: true,
      message: 'movie_details.seats_is_not_available',
      icon: (
        <Icon24 icon='/icons/booking/seat-warning.svg' title='alt.seat_warning_icon' />
      ),
      type: 'info',
    });
  };

  const handleSessionFilterLabel = (_session: Session, index: number): ReactNode => {
    const hour = DateTime.fromISO(_session.showtime, {}).toFormat('h:mm');
    const hourDayLight = DateTime.fromISO(_session.showtime, {
      locale,
    }).toFormat('a');

    const fontColor = (): string => {
      if (index === activeTab) return theme.palette.common.white;
      if (_session.isDisabled) return theme.palette.grey[40];
      return theme.palette.grey[80];
    };

    const ThreeDfontColor = (): string => {
      if (index === activeTab) return theme.palette.common.white;
      if (_session.isDisabled) return theme.palette.grey[40];
      else return theme.palette.secondary.main;
      return theme.palette.grey[80];
    };

    const afterMidnightFontColor = (): string => {
      if (index === activeTab) return theme.palette.common.white;
      if (_session.isDisabled) return theme.palette.grey[40];
      return theme.palette.primary.main;
    };

    return (
      <Stack>
        <Typography
          sx={{
            fontWeight: 400,
            fontSize: pxToRem(16),
            LineHeight: pxToRem(24),
            color: fontColor(),
            whiteSpace: 'nowrap',
          }}
        >
          {hour}
          &nbsp;
          {hourDayLight}
        </Typography>
        {!_session.isDisabled && (
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: pxToRem(9),
              LineHeight: pxToRem(24),
              color: afterMidnightFontColor(),
              whiteSpace: 'nowrap',
              textTransform: 'capitalize',
            }}
          >
            {checkIfAfterMidNight(_session)}
          </Typography>
        )}
        {_session.isThreeD && !_session.isDisabled && (
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: pxToRem(12),
              LineHeight: pxToRem(24),
              color: ThreeDfontColor(),
              whiteSpace: 'nowrap',
              textTransform: 'capitalize',
            }}
          >
            3D
          </Typography>
        )}
        {_session.isDisabled && (
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: pxToRem(12),
              LineHeight: pxToRem(24),
              color: fontColor(),
              whiteSpace: 'nowrap',
              textTransform: 'capitalize',
            }}
          >
            <FormattedMessage id='experience.sold.out' />
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <MuviTabs
      slideSx={{
        width: {
          mobile: '82px',
          tablet: pxToRem(111),
        },
      }}
      swiperSx={{
        px: '10px',
      }}
      initialSlide={isNumber(activeTab) ? activeTab : 0}
      centerInsufficientSlides
      slideToClickedSlide
      slidesPerGroupAuto
    >
      {sessions?.map((session, index) => (
        <Box
          key={session.id}
          id={`session-${experienceName}-${index}`}
          component='button'
          sx={{
            width: '100%',
            // minWidth: 'unset',
            // minHeight: 'unset',
            // transitionDuration: '100ms',
            // display: 'flex',
            // justifyContent: 'center',
            // alignItems: 'center',
            height: `${pxToRem(55)} !important`,
            background: theme.palette.lightGrey.main,
            borderRadius: '10px',
            border: 'none',
            fontSize: '1rem',
            cursor: 'pointer',
            ...(activeTab === index && {
              color: `${theme.palette.common.white} !important`,
              backgroundColor: theme.palette.primary.main,
            }),
          }}
          onClick={() =>
            handleSelectTime(session, Boolean(checkIfAfterMidNight(session)))
          }
        >
          {handleSessionFilterLabel(session, index)}
        </Box>
      ))}
    </MuviTabs>
  );
};

export default TimesFilter;
